import { faGripLinesVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { TableProps } from 'antd';
import { Table } from 'antd';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const MIN_COLUMN_WIDTH = 50;
const STORAGE_PREFIX = 'dnawiz-table-col-widths:';

type ColumnWidths = Record<string, number>;

type ResizableTableProps<RecordType> = TableProps<RecordType> & {
  /** Saves column widths to localStorage under this key. */
  persistenceKey?: string;
  /**
   * Keeps the total column width inside the table's container (e.g. a
   * resizable modal): drags are clamped to the remaining space and all
   * column widths scale automatically when the container is resized.
   */
  fitToContainer?: boolean;
};

type ResizeStartHandler = (
  columnKey: React.Key,
  tableElement: HTMLElement | null,
) => void;
type ResizeMoveHandler = (columnKey: React.Key, width: number) => void;
type ResizeEndHandler = () => void;
type ResizeResetHandler = (columnKey: React.Key) => void;

type ResizableHeaderCellProps = React.HTMLAttributes<HTMLTableCellElement> & {
  columnKey?: React.Key;
  isResizable?: boolean;
  isActive?: boolean;
  style?: React.CSSProperties;
  onColumnResizeStart?: ResizeStartHandler;
  onColumnResize?: ResizeMoveHandler;
  onColumnResizeEnd?: ResizeEndHandler;
  onColumnResizeReset?: ResizeResetHandler;
};

const loadPersistedWidths = (persistenceKey?: string): ColumnWidths => {
  if (!persistenceKey) {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + persistenceKey);
    return raw ? (JSON.parse(raw) as ColumnWidths) : {};
  } catch {
    return {};
  }
};

const storePersistedWidths = (
  persistenceKey: string | undefined,
  widths: ColumnWidths,
) => {
  if (!persistenceKey) {
    return;
  }
  try {
    window.localStorage.setItem(
      STORAGE_PREFIX + persistenceKey,
      JSON.stringify(widths),
    );
  } catch {
    // localStorage unavailable (private mode, quota) - ignore.
  }
};

const resolveColumnKey = <RecordType,>(
  column: ColumnType<RecordType>,
  index: number,
): React.Key => {
  if (column.key != null) {
    return column.key;
  }
  const { dataIndex } = column;
  if (typeof dataIndex === 'string' || typeof dataIndex === 'number') {
    return dataIndex;
  }
  if (Array.isArray(dataIndex)) {
    return dataIndex.join('.');
  }
  return index;
};

/** Flattened leaf columns in rendered order (group headers expanded). */
const flattenLeafColumns = <RecordType,>(
  columns: ColumnsType<RecordType>,
): ColumnType<RecordType>[] =>
  columns.flatMap((column) =>
    'children' in column && column.children?.length
      ? flattenLeafColumns(column.children)
      : [column as ColumnType<RecordType>],
  );

const getLeafKeys = <RecordType,>(
  columns?: ColumnsType<RecordType>,
): string[] =>
  flattenLeafColumns(columns ?? []).map((leaf, index) =>
    String(resolveColumnKey(leaf, index)),
  );

/**
 * Excel-style redistribution keeping the total constant: growing a column
 * takes space from the columns to its right (down to the minimum width),
 * shrinking it hands the space to the next column.
 */
const redistributeWidths = (
  base: number[],
  index: number,
  desired: number,
  mins: number[],
): number[] => {
  const next = [...base];
  const delta = desired - base[index];
  if (delta > 0) {
    let remaining = delta;
    for (let i = index + 1; i < next.length && remaining > 0; i += 1) {
      const take = Math.min(remaining, Math.max(0, next[i] - mins[i]));
      next[i] -= take;
      remaining -= take;
    }
    next[index] = base[index] + (delta - remaining);
  } else if (delta < 0) {
    const recipient = index + 1 < next.length ? index + 1 : index - 1;
    if (recipient >= 0) {
      const give = Math.min(-delta, Math.max(0, base[index] - mins[index]));
      next[index] = base[index] - give;
      next[recipient] += give;
    }
  }
  return next;
};

/**
 * Scales widths proportionally so they sum up to exactly the container while
 * every column keeps at least its minimum width (columns that would fall
 * below it are pinned at the minimum, the rest share what is left).
 */
const fitWidthsToContainer = (
  input: number[],
  containerWidth: number,
  mins: number[],
): number[] => {
  if (!input.length) {
    return input;
  }
  const pinned = input.map(() => false);
  let changed = true;
  while (changed) {
    changed = false;
    let pinnedTotal = 0;
    let freeTotal = 0;
    for (let i = 0; i < input.length; i += 1) {
      if (pinned[i]) {
        pinnedTotal += mins[i];
      } else {
        freeTotal += input[i];
      }
    }
    const scale = freeTotal
      ? Math.max(0, containerWidth - pinnedTotal) / freeTotal
      : 0;
    for (let i = 0; i < input.length; i += 1) {
      if (!pinned[i] && input[i] * scale < mins[i]) {
        pinned[i] = true;
        changed = true;
      }
    }
  }
  let pinnedTotal = 0;
  let freeTotal = 0;
  let lastFree = -1;
  input.forEach((width, i) => {
    if (pinned[i]) {
      pinnedTotal += mins[i];
    } else {
      freeTotal += width;
      lastFree = i;
    }
  });
  const scale = freeTotal
    ? Math.max(0, containerWidth - pinnedTotal) / freeTotal
    : 0;
  const result = input.map((width, i) =>
    pinned[i] ? mins[i] : Math.max(mins[i], Math.round(width * scale)),
  );
  if (lastFree >= 0) {
    const difference = containerWidth - result.reduce((s, w) => s + w, 0);
    result[lastFree] = Math.max(mins[lastFree], result[lastFree] + difference);
  }
  return result;
};

const MIN_FLEX_WIDTH = 150;

/** Columns may shrink to 75% of their declared width, never further. */
const MIN_DECLARED_RATIO = 0.75;

const getDeclaredWidths = <RecordType,>(
  columns: ColumnsType<RecordType> | undefined,
): (number | null)[] =>
  flattenLeafColumns(columns ?? []).map((leaf) =>
    typeof leaf.width === 'number' ? leaf.width : null,
  );

const getMinWidths = <RecordType,>(
  columns: ColumnsType<RecordType> | undefined,
): number[] =>
  getDeclaredWidths(columns).map((width) =>
    width == null
      ? MIN_FLEX_WIDTH
      : Math.max(MIN_COLUMN_WIDTH, Math.round(width * MIN_DECLARED_RATIO)),
  );

/**
 * Initial layout for fitToContainer: columns with a numeric `width` keep it,
 * columns without one share the remaining space. Null if that isn't possible.
 */
const computeDeclaredWidths = <RecordType,>(
  columns: ColumnsType<RecordType> | undefined,
  containerWidth: number,
): number[] | null => {
  const declared = getDeclaredWidths(columns);
  if (!declared.length) {
    return null;
  }
  const fixedTotal = declared.reduce<number>(
    (sum, width) => sum + (width ?? 0),
    0,
  );
  const flexCount = declared.filter((width) => width == null).length;
  if (!flexCount) {
    return declared as number[];
  }
  // Not scaled to the container: these are the preferred widths, which
  // absorbContainerChange fits to whatever size the container has.
  const share = Math.max(
    MIN_FLEX_WIDTH,
    Math.floor((containerWidth - fixedTotal) / flexCount),
  );
  return declared.map((width) => width ?? share);
};

/**
 * Container got wider/narrower: the flexible (main content) column absorbs
 * the difference so the other columns keep their preferred size. Falls back
 * to proportional scaling if that column would become too narrow.
 */
const absorbContainerChange = (
  current: number[],
  containerWidth: number,
  declared: (number | null)[],
  mins: number[],
): number[] => {
  const total = current.reduce((sum, width) => sum + width, 0);
  // Prefer the column without a declared width (the flexible one).
  const flexIndex = declared.findIndex(
    (width, index) => width == null && index < current.length,
  );
  const target =
    flexIndex >= 0 ? flexIndex : current.indexOf(Math.max(...current));
  const adjusted = current[target] + (containerWidth - total);
  if (adjusted < MIN_FLEX_WIDTH) {
    return fitWidthsToContainer(current, containerWidth, mins);
  }
  const next = [...current];
  next[target] = adjusted;
  return next;
};

/** Header cell with a drag handle on its right edge. Double-click resets. */
const ResizableHeaderCell = ({
  columnKey,
  isResizable,
  isActive,
  onColumnResizeStart,
  onColumnResize,
  onColumnResizeEnd,
  onColumnResizeReset,
  children,
  style,
  ...restProps
}: ResizableHeaderCellProps) => {
  const handleMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
    if (columnKey == null || !onColumnResize || !onColumnResizeStart) {
      return;
    }
    // Stop antd sorting and text selection.
    event.preventDefault();
    event.stopPropagation();

    const th = event.currentTarget.closest('th');
    const tableElement = event.currentTarget.closest(
      '.ant-table',
    ) as HTMLElement | null;
    const startWidth = th?.getBoundingClientRect().width ?? MIN_COLUMN_WIDTH;
    const startX = event.clientX;

    onColumnResizeStart(columnKey, tableElement);
    document.body.classList.add('resizable-table--resizing');

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const nextWidth = Math.max(
        MIN_COLUMN_WIDTH,
        Math.round(startWidth + moveEvent.clientX - startX),
      );
      onColumnResize(columnKey, nextWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove('resizable-table--resizing');
      onColumnResizeEnd?.();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleDoubleClick = (event: React.MouseEvent<HTMLSpanElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (columnKey != null) {
      onColumnResizeReset?.(columnKey);
    }
  };

  const handleClassName = isActive
    ? 'resizable-table__handle resizable-table__handle--active'
    : 'resizable-table__handle';

  return (
    <th {...restProps} style={{ ...style, position: 'relative' }}>
      {children}
      {isResizable && columnKey != null && (
        <span
          className={handleClassName}
          role="separator"
          aria-orientation="vertical"
          aria-label="Drag to resize column, double-click to reset"
          onMouseDown={handleMouseDown}
          onClick={(event) => event.stopPropagation()}
          onDoubleClick={handleDoubleClick}
        >
          <FontAwesomeIcon
            icon={faGripLinesVertical}
            aria-hidden="true"
            style={{ width: 8, height: 16 }}
          />
        </span>
      )}
    </th>
  );
};

type ResizeHandlers = {
  onStart: ResizeStartHandler;
  onMove: ResizeMoveHandler;
  onEnd: ResizeEndHandler;
  onReset: ResizeResetHandler;
};

const applyResizableColumns = <RecordType,>(
  columns: ColumnsType<RecordType> | undefined,
  widths: ColumnWidths,
  originalWidths: ColumnWidths,
  hasUserResized: boolean,
  activeKey: React.Key | null,
  handlers: ResizeHandlers,
): ColumnsType<RecordType> | undefined =>
  columns?.map((column, index) => {
    const columnKey = resolveColumnKey(column as ColumnType<RecordType>, index);
    const next = { ...column } as ColumnType<RecordType> & {
      children?: ColumnsType<RecordType>;
    };

    const overrideWidth = widths[String(columnKey)];
    if (overrideWidth != null) {
      next.width = overrideWidth;
    } else if (hasUserResized) {
      // After resizing starts, keep each column at its pre-resize width
      // unless the user overrode it.
      const originalWidth = originalWidths[String(columnKey)];
      if (originalWidth != null) {
        next.width = originalWidth;
      }
    }

    const hasChildren =
      'children' in column &&
      Array.isArray(column.children) &&
      column.children.length > 0;
    if (hasChildren) {
      next.children = applyResizableColumns(
        column.children,
        widths,
        originalWidths,
        hasUserResized,
        activeKey,
        handlers,
      );
    }

    const existingHeaderCellProps = (
      column as ColumnType<RecordType>
    ).onHeaderCell?.(column as never, index);
    next.onHeaderCell = () =>
      ({
        ...existingHeaderCellProps,
        columnKey,
        isResizable: !hasChildren,
        isActive: activeKey === columnKey,
        onColumnResizeStart: handlers.onStart,
        onColumnResize: handlers.onMove,
        onColumnResizeEnd: handlers.onEnd,
        onColumnResizeReset: handlers.onReset,
      }) as React.HTMLAttributes<HTMLElement>;

    return next;
  });

/**
 * antd `Table` with Excel-style column resizing: drag the grip on a header's
 * right edge, double-click to reset. `persistenceKey` saves widths per user.
 */
const ResizableTable = <RecordType extends object = any>({
  columns,
  components,
  className,
  persistenceKey,
  tableLayout,
  scroll,
  fitToContainer,
  ...restProps
}: ResizableTableProps<RecordType>) => {
  const [widths, setWidths] = useState<ColumnWidths>(() =>
    loadPersistedWidths(persistenceKey),
  );
  // Column widths captured before the first resize, for double-click reset.
  const [originalWidths, setOriginalWidths] = useState<ColumnWidths>({});
  // False until first drag: untouched tables render like a plain antd Table.
  const [hasUserResized, setHasUserResized] = useState(false);
  const [activeKey, setActiveKey] = useState<React.Key | null>(null);

  // Ref mirrors of the width maps + container width: drag/observer callbacks
  // read them so they never work on stale render closures.
  const widthsRef = useRef<ColumnWidths>(widths);
  const originalWidthsRef = useRef<ColumnWidths>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidthRef = useRef(0);
  const dragBaseRef = useRef<number[] | null>(null);
  // fitToContainer: the widths the user/defaults want, independent of the
  // current container size. Displayed widths are derived from them.
  const preferredRef = useRef<number[] | null>(null);
  const columnsRef = useRef(columns);
  columnsRef.current = columns;

  const persist = useCallback(
    (next: ColumnWidths) => storePersistedWidths(persistenceKey, next),
    [persistenceKey],
  );

  // Remember each column's rendered width before the first drag, so
  // switching to fixed layout doesn't reshuffle them.
  const freezeColumnWidths = useCallback((tableElement: HTMLElement | null) => {
    // Leaf columns live in the last header row; skip antd's internal
    // selection/expand/scrollbar cells.
    const headerCells = tableElement?.querySelectorAll(
      '.ant-table-thead > tr:last-child > th',
    );
    if (!headerCells?.length || !columnsRef.current) {
      return;
    }
    const leafColumns = flattenLeafColumns(columnsRef.current);
    const next = { ...originalWidthsRef.current };
    let leafIndex = 0;
    headerCells.forEach((th) => {
      const cell = th as HTMLTableCellElement;
      if (
        cell.classList.contains('ant-table-selection-column') ||
        cell.classList.contains('ant-table-row-expand-icon-cell') ||
        cell.classList.contains('ant-table-cell-scrollbar')
      ) {
        return;
      }
      const span = cell.colSpan || 1;
      const leaf = leafColumns[leafIndex];
      if (leaf) {
        const key = String(resolveColumnKey(leaf, leafIndex));
        // First snapshot wins; later drags must not overwrite it.
        if (next[key] == null) {
          next[key] = Math.round(cell.getBoundingClientRect().width);
        }
      }
      leafIndex += span;
    });
    // Update the ref synchronously so drag/observer callbacks see it
    // immediately, then mirror it into state for rendering.
    originalWidthsRef.current = next;
    setOriginalWidths(next);
  }, []);

  const getEffectiveWidths = useCallback(
    (keys: string[]) =>
      keys.map(
        (key) => widthsRef.current[key] ?? originalWidthsRef.current[key],
      ),
    [],
  );

  const applyWidths = useCallback(
    (keys: string[], values: number[], remember = true) => {
      const next: ColumnWidths = {};
      keys.forEach((key, index) => {
        next[key] = values[index];
      });
      widthsRef.current = next;
      if (remember) {
        preferredRef.current = values;
        persist(next);
      }
      setWidths(next);
    },
    [persist],
  );

  const handleResizeStart = useCallback<ResizeStartHandler>(
    (columnKey, tableElement) => {
      freezeColumnWidths(tableElement);
      if (fitToContainer) {
        // Every move is computed from this snapshot, so dragging back and
        // forth is fully reversible.
        const effective = getEffectiveWidths(getLeafKeys(columnsRef.current));
        dragBaseRef.current = effective.every((width) => width != null)
          ? (effective as number[])
          : null;
      }
      setHasUserResized(true);
      setActiveKey(columnKey);
    },
    [fitToContainer, freezeColumnWidths, getEffectiveWidths],
  );

  const handleColumnResize = useCallback<ResizeMoveHandler>(
    (columnKey, width) => {
      const keys = getLeafKeys(columnsRef.current);
      const index = keys.indexOf(String(columnKey));
      const base = dragBaseRef.current;
      if (fitToContainer && base && base.length === keys.length && index >= 0) {
        applyWidths(
          keys,
          redistributeWidths(
            base,
            index,
            width,
            getMinWidths(columnsRef.current),
          ),
        );
        return;
      }
      const next = { ...widthsRef.current, [String(columnKey)]: width };
      widthsRef.current = next;
      persist(next);
      setWidths(next);
    },
    [applyWidths, fitToContainer, persist],
  );

  const handleResizeEnd = useCallback<ResizeEndHandler>(() => {
    setActiveKey(null);
  }, []);

  const handleResizeReset = useCallback<ResizeResetHandler>(
    (columnKey) => {
      const key = String(columnKey);
      if (fitToContainer && containerWidthRef.current) {
        const keys = getLeafKeys(columnsRef.current);
        const index = keys.indexOf(key);
        const original = originalWidthsRef.current[key];
        const effective = getEffectiveWidths(keys);
        if (
          index >= 0 &&
          original != null &&
          effective.every((width) => width != null)
        ) {
          const candidate = (effective as number[]).map((width, i) =>
            i === index ? original : width,
          );
          applyWidths(
            keys,
            fitWidthsToContainer(
              candidate,
              containerWidthRef.current,
              getMinWidths(columnsRef.current),
            ),
          );
          return;
        }
      }
      const next = { ...widthsRef.current };
      delete next[key];
      widthsRef.current = next;
      persist(next);
      setWidths(next);
    },
    [applyWidths, fitToContainer, getEffectiveWidths, persist],
  );

  // fitToContainer: watch the container (e.g. a resizable modal) and scale
  // every explicit column width with it, so the table always fills — but
  // never exceeds — the available width.
  useEffect(() => {
    if (!fitToContainer) {
      return;
    }
    const element = containerRef.current;
    if (!element) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const containerWidth = Math.round(entries[0]?.contentRect.width ?? 0);
      containerWidthRef.current = containerWidth;
      if (!containerWidth) {
        return;
      }
      const keys = getLeafKeys(columnsRef.current);
      let preferred = preferredRef.current;
      if (!preferred || preferred.length !== keys.length) {
        const known = getEffectiveWidths(keys);
        if (known.every((width) => width != null)) {
          // Widths restored from localStorage.
          preferred = known as number[];
        } else {
          preferred = computeDeclaredWidths(columnsRef.current, containerWidth);
          if (!preferred) {
            return;
          }
          const initialMap: ColumnWidths = {};
          keys.forEach((key, index) => {
            initialMap[key] = (preferred as number[])[index];
          });
          originalWidthsRef.current = initialMap;
          setOriginalWidths(initialMap);
        }
        preferredRef.current = preferred;
      }
      const next = absorbContainerChange(
        preferred,
        containerWidth,
        getDeclaredWidths(columnsRef.current),
        getMinWidths(columnsRef.current),
      );
      const shown = getEffectiveWidths(keys);
      if (next.every((width, index) => width === shown[index])) {
        return;
      }
      // Derived from the preferred widths: shown only, never remembered, so
      // a temporarily small container can't squeeze columns permanently.
      applyWidths(keys, next, false);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [applyWidths, fitToContainer, freezeColumnWidths, getEffectiveWidths]);

  const handlers = useMemo<ResizeHandlers>(
    () => ({
      onStart: handleResizeStart,
      onMove: handleColumnResize,
      onEnd: handleResizeEnd,
      onReset: handleResizeReset,
    }),
    [handleResizeStart, handleColumnResize, handleResizeEnd, handleResizeReset],
  );

  const mergedColumns = useMemo(
    () =>
      applyResizableColumns(
        columns,
        widths,
        originalWidths,
        hasUserResized,
        activeKey,
        handlers,
      ),
    [columns, widths, originalWidths, hasUserResized, activeKey, handlers],
  );

  const mergedComponents = useMemo(
    () => ({
      ...components,
      header: {
        ...components?.header,
        cell: ResizableHeaderCell,
      },
    }),
    [components],
  );

  const mergedClassName = className
    ? `resizable-table ${className}`
    : 'resizable-table';

  // True once explicit column widths are active (a live drag or widths
  // restored from localStorage). Fixed layout honors them exactly. Regular
  // tables get a horizontal scroll area (scroll.x) so widened columns never
  // push the page beyond its edge. fitToContainer tables never scroll: their
  // widths always sum to the container width instead.
  const hasExplicitWidths = hasUserResized || Object.keys(widths).length > 0;
  const mergedScroll =
    hasExplicitWidths && !fitToContainer
      ? { x: 'max-content', ...scroll }
      : scroll;

  return (
    <div
      ref={containerRef}
      className={
        fitToContainer
          ? 'resizable-table__container resizable-table__container--fit'
          : 'resizable-table__container'
      }
    >
      <Table<RecordType>
        {...restProps}
        className={mergedClassName}
        columns={mergedColumns}
        components={mergedComponents}
        scroll={mergedScroll}
        // Fixed layout (needed for exact widths) only once widths exist.
        tableLayout={hasExplicitWidths ? 'fixed' : tableLayout}
      />
    </div>
  );
};

export default ResizableTable;
